/** @jsxImportSource @emotion/react */
import { faListCheck } from "@fortawesome/pro-regular-svg-icons";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import {
    Modal, ModalBody, ModalFooter, ModalHeader
} from "reactstrap";
import { RootState } from "../../../reducers";
import { listFacilityLocations } from "../../../actions/FacilityLocations/actions";
import { defaultLayer } from "../../../actions/Layers/constants";
import { applyDisplayFilterFields } from "../../../actions/Tenants/config/applyDisplayFilter";
import useConfig from "../../../actions/Tenants/config/configHook";
import { useAddLayerAllocationMutation } from "../../../hooks/mutations";
import { useCurrentLayer } from "../../../hooks/useCurrentEntities";
import usePatchLayer from "../../../hooks/usePatchLayer";
import { isFilledArray } from "../../../utils";
import useNavigateFrom from "../../../utils/useNavigateFrom";
import MetaForm, { metaFormIsValid } from "../../Forms/MetaForm";
import { Button } from "../../Helper/Button";
import { Loading } from "../../Helper/Loading";
import { useSwitchTabContext } from "./fields/useSwitchTab";

interface LayerListCtaProps {
    selectedLayers?: string[];
    deselectAll?: () => void;
}

/**
 * Unified component for editing a single layer or multiple selected layers.
 * Mode is derived from the URL parameter `cta_layer_id`:
 * - "bulk" → bulk mode (uses blank form, applies to selected layers)
 * - Valid layer ID → single mode (fetches layer data, initializes form from layer state)
 * - Empty/null → modal closed
 */
function LayerListCtaComponent({ selectedLayers = [], deselectAll }: LayerListCtaProps) {
    const config = useConfig();
    const dispatch = useDispatch();
    const patchLayer = usePatchLayer();
    const addLayerAllocationMutation = useAddLayerAllocationMutation();
    const [searchParams, setSearchParams] = useSearchParams();
    const params = useParams();
    const tenantId = params.tenant_id as string;
    const { activeTab } = useSwitchTabContext();

    const [isOpen, toggle] = useState(false);
    const [allocateFormState, setAllocateFormState] = useState({} as any);
    const [patchFormState, setPatchFormState] = useState({} as any);

    const locations = useSelector((state: RootState) => state.facilityLocations.locations);
    const meta_form = applyDisplayFilterFields(activeTab?.cta_form, {});

    const cta_layer_id = searchParams.get("cta_layer_id");
    const isBulkMode = cta_layer_id === "bulk";
    const isSingleMode = cta_layer_id && cta_layer_id !== "bulk";
    const currentLayerQuery = useCurrentLayer(tenantId, cta_layer_id || undefined, Boolean(isSingleMode));
    // * This modal opens from the overview, so there is no layer route param to read from.
    const layer = currentLayerQuery.data ?? defaultLayer;
    const layer_config = config.get_layer_config(layer);

    const normalizeUrl = () => {
        searchParams.delete("cta_layer_id");
        setSearchParams(searchParams);
    };

    const onClose = () => {
        normalizeUrl();
        if (isBulkMode && deselectAll) {
            deselectAll();
        }
    };

    // Initialize form based on cta_layer_id
    useEffect(() => {
        if (isSingleMode && cta_layer_id) {
            toggle(true);
            dispatch(listFacilityLocations({ offset: 0, limit: 200 }) as any);
            setAllocateFormState({});
            setPatchFormState({});
        } else if (isBulkMode) {
            toggle(true);
            dispatch(listFacilityLocations({ offset: 0, limit: 200 }) as any);
            setAllocateFormState({});
            setPatchFormState({});
        } else {
            // Modal closed - reset state
            setPatchFormState({});
            setAllocateFormState({});
            toggle(false);
        }
    }, [cta_layer_id, isSingleMode, isBulkMode, dispatch]);

    useEffect(() => {
        if (currentLayerQuery.data) {
            setAllocateFormState(currentLayerQuery.data.latest_location || {});
            setPatchFormState(currentLayerQuery.data);
        } else if (currentLayerQuery.isError) {
            setPatchFormState({});
            setAllocateFormState({});
        }
    }, [currentLayerQuery.data, currentLayerQuery.isError]);

    const allocate_form = [
        {
            name: "location_id",
            label: "Location",
            type: locations.length > 6 ? "single-select" : "radio",
            options: locations.map((i) => ({ ...i, value: i.location_id })),
            onchange_callback: ({ value }) => {
                const location = locations.find((i) => i.location_id === value);
                if (location) {
                    setAllocateFormState(location);
                } else {
                    setAllocateFormState({});
                }
            }
        }
    ];

    const onSubmit = () => {
        if (isSingleMode) {
            // Single layer submission
            if (meta_form && !metaFormIsValid(meta_form, layer)) return;

            if (allocateFormState.location_id) {
                addLayerAllocationMutation.mutate({
                    tenantId,
                    layerId: layer.id,
                    stage: allocateFormState.stage,
                    locationId: allocateFormState.location_id,
                    includeChildren: false
                });
            }

            Object.keys(patchFormState).forEach((key) => {
                if (patchFormState[key] !== undefined) {
                    patchLayer(layer, key, patchFormState[key], true);
                }
            });
        } else {
            // Bulk submission
            const { stage, location_id } = allocateFormState;

            Promise.all(selectedLayers.flatMap((layer_id) => [
                (stage || location_id) ? addLayerAllocationMutation.mutateAsync({ tenantId, layerId: layer_id, stage, locationId: location_id, includeChildren: false }) : Promise.resolve(),
                ...meta_form.map((field) => {
                    if (patchFormState[field.name] !== undefined || patchFormState[field.name] === "") {
                        return patchLayer(layer, field.name, patchFormState[field.name], true);
                    }
                    return Promise.resolve();
                })
            ])).then(() => {
                if (deselectAll) {
                    deselectAll();
                }
            });
        }

        normalizeUrl();
    };

    const loading = isSingleMode && (!layer_config || !layer);
    const itemCount = isBulkMode ? selectedLayers.length : 1;
    const headerTitle = isSingleMode
        ? `${activeTab?.cta_form_title} ${layer_config?.text} ${layer?.label}`
        : `${activeTab?.cta_form_title} ${itemCount} items`;
    const submitButtonText = isSingleMode ? "Save" : `Update ${itemCount} items`;
    const submitButtonColor = isSingleMode ? "success" : "primary";
    const submitDisabled = isBulkMode && !isFilledArray(selectedLayers);

    return (
        <Modal isOpen={isOpen} size="md">
            {loading && <Loading />}

            {!loading && (
                <>
                    <ModalHeader toggle={onClose} onClick={onClose}>
                        {headerTitle}
                    </ModalHeader>

                    <ModalBody>
                        <MetaForm
                            meta={allocate_form}
                            object={allocateFormState}
                            config={config}
                            setValue={(field, value) => setAllocateFormState((prev) => ({ ...prev, [field]: value }))}
                        />

                        <MetaForm
                            meta={meta_form}
                            object={patchFormState}
                            config={config}
                            setValue={(field, value) => setPatchFormState((prev) => ({ ...prev, [field]: value }))}
                        />
                    </ModalBody>

                    <ModalFooter>
                        <Button color="light" onClick={onClose}>Cancel</Button>
                        <Button color={submitButtonColor} disabled={submitDisabled} onClick={onSubmit}>
                            {submitButtonText}
                        </Button>
                    </ModalFooter>
                </>
            )}
        </Modal>
    );
}

// * Single layer CTA - reads from URL params
const LayerListCta = () => {
    return <LayerListCtaComponent />;
};

export default LayerListCta;

// * Bulk layer CTA - takes selected layers as prop
export function BulkLayerListCTA({ selectedLayers, deselectAll }: { selectedLayers: string[], deselectAll: any }) {
    return <LayerListCtaComponent selectedLayers={selectedLayers} deselectAll={deselectAll} />;
}

// * Button to open the bulk CTA form from TabActions
export const BulkLayerListCTAButton = () => {
    const { navigateTo } = useNavigateFrom();
    const { selectedLayersArray, activeTab, tab_category } = useSwitchTabContext();

    if (!activeTab?.cta_form_title) {
        return null;
    }
    return (
        <Button
            color={selectedLayersArray.length > 0 ? "primary" : "secondary"}
            disabled={selectedLayersArray.length === 0}
            onClick={() => navigateTo(`/layer/tab/${tab_category}/${activeTab?.value}`, true, { cta_layer_id: "bulk" }, true)}
            icon={faListCheck}
        >
            {activeTab?.cta_form_title} {selectedLayersArray.length} items
        </Button>
    );
};
